import { create } from 'zustand';
import { FeedbackSubmission, FeedbackSource } from '@/types';
import { apiService } from '@/services/api.service';
import { AsyncState } from './types';

interface FeedbackState extends AsyncState {
  // Whether the user has successfully submitted feedback in this session
  submitted: boolean;
  // Which source triggered the modal (set before opening)
  activeSource: FeedbackSource | null;
  // Whether the modal is currently open
  isOpen: boolean;

  // Actions
  openFeedback: (source: FeedbackSource) => void;
  closeFeedback: () => void;
  submitFeedback: (submission: FeedbackSubmission, accessToken: string) => Promise<void>;
  reset: () => void;
}

const initialState = {
  loading: false,
  error: null,
  submitted: false,
  activeSource: null as FeedbackSource | null,
  isOpen: false,
};

export const useFeedbackStore = create<FeedbackState>((set) => ({
  ...initialState,

  /**
   * Open the feedback modal from a given source location
   */
  openFeedback: (source: FeedbackSource) => {
    set({ isOpen: true, activeSource: source, error: null });
  },

  /**
   * Close the feedback modal without submitting
   */
  closeFeedback: () => {
    set({ isOpen: false });
  },

  /**
   * Submit feedback to the API. On success, marks as submitted and closes modal.
   */
  submitFeedback: async (submission: FeedbackSubmission, accessToken: string) => {
    set({ loading: true, error: null });
    try {
      await apiService.submitFeedback(submission, accessToken);
      set({ loading: false, submitted: true, isOpen: false });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to submit feedback';
      set({ loading: false, error: errorMessage });
      throw error;
    }
  },

  /**
   * Reset the store to initial state (e.g. for testing or forced re-prompt)
   */
  reset: () => set(initialState),
}));
